import dayjs from 'dayjs'
import { ResolvableEvent, expandOccurrences } from 'common/scheduleResolver'

// A screen keeps a week of upcoming content on hand, so it can lose its connection for days and
// still switch to every event on time.
export const PREFETCH_HORIZON_DAYS = 7

// Matches MAX_CANVAS_NESTING_DEPTH in CanvasLayoutPlayer: deeper layouts are never rendered.
const MAX_NESTING_DEPTH = 3

export type FetchableType = 'playlist' | 'canvasLayout'

export interface TargetRef {
  type: FetchableType | 'content'
  id: string
}

export interface ScheduleWithTargets<Target extends TargetRef> {
  events: Array<ResolvableEvent & { target: Target | null }>
  fallback: Target | null
}

const refKey = (ref: TargetRef) => `${ref.type}:${ref.id}`

/** Playlists and layouts the screen can play from now until the horizon, the fallback first. */
export function upcomingTargets<Target extends TargetRef>(
  schedule: ScheduleWithTargets<Target>,
  now: number,
  timezone: string,
  horizonDays = PREFETCH_HORIZON_DAYS
): Target[] {
  const from = dayjs(now)
  const to = from.add(horizonDays, 'day')
  const upcoming = schedule.events
    .filter((event) => event.target && expandOccurrences(event, from, to, timezone).length > 0)
    .map((event) => event.target as Target)

  const seen = new Set<string>()
  return [schedule.fallback, ...upcoming].filter((target): target is Target => {
    if (!target || target.type === 'content' || seen.has(refKey(target))) return false
    seen.add(refKey(target))
    return true
  })
}

interface PlaylistLike {
  items?: Array<{ canvasLayout?: { id: string } | null } | null> | null
}

interface LayoutLike {
  canvasData?: { objects?: Array<{ type?: string; playlistId?: string } | null> | null } | null
}

/** What a playlist or layout loads when it renders: layouts inside playlists, playlists inside layouts. */
export function nestedRefs(type: FetchableType, data: unknown): Array<{ type: FetchableType; id: string }> {
  if (type === 'playlist') {
    return ((data as PlaylistLike)?.items ?? [])
      .map((item) => item?.canvasLayout?.id)
      .filter((id): id is string => !!id)
      .map((id) => ({ type: 'canvasLayout' as const, id }))
  }
  return ((data as LayoutLike)?.canvasData?.objects ?? [])
    .map((object) => (object?.type === 'playlist' ? object.playlistId : undefined))
    .filter((id): id is string => !!id)
    .map((id) => ({ type: 'playlist' as const, id }))
}

export interface PrefetchIo {
  /** The SWR key the player component uses for this target. */
  keyFor: (ref: { type: FetchableType; id: string }) => string
  load: (key: string) => Promise<unknown>
  store: (key: string, data: unknown) => void
}

/**
 * Loads every target and everything nested inside it into the player's cache. A failed fetch is
 * skipped rather than thrown: whatever was cached before stays and the rest still loads.
 * Returns the keys that are now available.
 */
export async function prefetchTargets(
  targets: Array<{ type: FetchableType; id: string }>,
  io: PrefetchIo
): Promise<Set<string>> {
  const loaded = new Set<string>()
  const visited = new Set<string>()

  const visit = async (ref: { type: FetchableType; id: string }, layoutsAbove: number) => {
    const key = io.keyFor(ref)
    if (visited.has(key) || (ref.type === 'canvasLayout' && layoutsAbove >= MAX_NESTING_DEPTH)) return
    visited.add(key)
    let data: unknown
    try {
      data = await io.load(key)
    } catch {
      return
    }
    io.store(key, data)
    loaded.add(key)
    const childLayoutsAbove = ref.type === 'canvasLayout' ? layoutsAbove + 1 : layoutsAbove
    await Promise.all(nestedRefs(ref.type, data).map((nested) => visit(nested, childLayoutsAbove)))
  }

  await Promise.all(targets.map((target) => visit(target, 0)))
  return loaded
}
